<template>
  <Teleport to="body">
    <Transition
      name="confirm-fade"
      appear
    >
      <div
        v-if="active"
        class="modal-overlay confirm-overlay"
        role="dialog"
        aria-modal="true"
        :aria-labelledby="titleId"
        :aria-describedby="messageId"
        @click.self="onCancel"
        @keydown.esc.stop="onCancel"
      >
        <div
          ref="dialogEl"
          class="confirm-dialog"
          tabindex="-1"
        >
          <h2
            :id="titleId"
            class="confirm-title"
          >
            {{ active.title }}
          </h2>
          <p
            :id="messageId"
            class="confirm-message"
          >
            {{ active.message }}
          </p>

          <div class="confirm-actions">
            <button
              v-if="!active.alertOnly"
              type="button"
              class="btn-tertiary confirm-btn-cancel"
              @click="onCancel"
            >
              {{ active.cancelLabel }}
            </button>
            <button
              ref="confirmBtn"
              type="button"
              :class="['confirm-btn-confirm', active.destructive ? 'is-destructive' : 'btn-primary']"
              @click="onConfirm"
            >
              {{ active.confirmLabel }}
            </button>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { computed, nextTick, ref, watch } from "vue"
import { storeToRefs } from "pinia"
import { useConfirmStore } from "@/stores/confirm"

const store = useConfirmStore()
const { active } = storeToRefs(store)

const dialogEl = ref<HTMLDivElement | null>(null)
const confirmBtn = ref<HTMLButtonElement | null>(null)

const titleId = computed(() => "confirm-title")
const messageId = computed(() => "confirm-message")

// Move focus to the confirm button when the dialog opens, so Enter activates
// it and screen readers announce the action.
watch(active, async (v) => {
  if (!v) return
  await nextTick()
  confirmBtn.value?.focus()
})

function onCancel() {
  store.answer(false)
}
function onConfirm() {
  store.answer(true)
}
</script>

<style scoped>
.confirm-overlay {
  align-items: center;
  justify-content: center;
  padding: 1.25rem;
}

.confirm-dialog {
  background-color: var(--color-surface-container-lowest);
  border-radius: 1.25rem;
  padding: 1.5rem 1.5rem 1.125rem;
  width: 100%;
  max-width: 22rem;
  box-shadow: 0 24px 64px rgba(10, 44, 42, 0.28);
  outline: none;
}

.confirm-title {
  font-family: 'Manrope', sans-serif;
  font-weight: 800;
  font-size: 1.0625rem;
  color: var(--color-on-surface);
  margin: 0 0 0.5rem;
  line-height: 1.3;
}

.confirm-message {
  font-family: 'Inter', sans-serif;
  font-size: 0.9375rem;
  color: var(--color-on-surface-variant);
  line-height: 1.45;
  margin: 0 0 1.25rem;
}

.confirm-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
}

.confirm-btn-cancel {
  color: var(--color-on-surface-variant);
}

.confirm-btn-confirm {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.625rem 1.25rem;
  border-radius: 9999px;
  border: none;
  cursor: pointer;
  font-family: 'Inter', sans-serif;
  font-size: 0.875rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  transition: opacity 0.15s, transform 0.1s, background-color 0.15s;
}
.confirm-btn-confirm:hover { transform: translateY(-1px); }
.confirm-btn-confirm:active { transform: translateY(0); }

.confirm-btn-confirm.is-destructive {
  background: linear-gradient(135deg, var(--color-error) 0%, var(--color-error-container) 100%);
  color: var(--color-on-error);
}
.confirm-btn-confirm.is-destructive:hover { opacity: 0.92; }

/* Fade + scale entrance so the dialog reads as a focused, deliberate prompt
   rather than a popup that snaps in. */
.confirm-fade-enter-active {
  transition: opacity 0.18s ease;
}
.confirm-fade-leave-active {
  transition: opacity 0.14s ease;
}
.confirm-fade-enter-from,
.confirm-fade-leave-to {
  opacity: 0;
}
.confirm-fade-enter-active .confirm-dialog {
  animation: confirm-pop 0.18s ease;
}

@keyframes confirm-pop {
  from { transform: scale(0.94); opacity: 0; }
  to   { transform: scale(1); opacity: 1; }
}
</style>
